import { useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "bg-[#0B0F19] border border-[#D4AF37]/25 rounded-xl px-3 py-2 text-sm text-starlight outline-none focus:border-[#D4AF37]";
const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

export function parseLines(text) {
  const seen = new Set();
  return text.split(/\r?\n/).map((raw) => raw.trim()).filter(Boolean).map((raw) => {
    const email = (raw.match(/[^\s<>,;]+@[^\s<>,;]+/) || [""])[0].toLowerCase();
    const name = raw.replace(email, "").replace(/[<>,;\t]/g, " ").replace(/\s+/g, " ").trim() || email.split("@")[0];
    let error = "";
    if (!EMAIL.test(email)) error = "no valid email";
    else if (seen.has(email)) error = "duplicate";
    seen.add(email);
    return { raw, name: name.slice(0, 60), email, error };
  });
}

export default function BulkPasses({ token, onSent }) {
  const [text, setText] = useState("");
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => parseLines(text), [text]);
  const good = rows.filter((r) => !r.error);
  const tooMany = good.length > 50;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/analytics/passes/bulk`, { people: good.map(({ name, email }) => ({ name, email })), months: Number(months), note },
        { headers: { "X-Analytics-Token": token } });
      toast.success(`${data.created} passes are being emailed${data.skipped.length ? ` · ${data.skipped.length} skipped (already have an unclaimed pass)` : ""}`);
      setText(""); setNote("");
      onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Couldn't send the passes"); }
    setBusy(false);
  };
  return (
    <section className="bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-4 mb-6" data-testid="bulk-passes">
      <h3 className="text-base text-starlight flex items-center gap-2 mb-1"><Users size={16} className="gold-text" /> Bulk send</h3>
      <p className="text-xs text-[#A7B0C0] mb-3">Paste one person per line, e.g. <span className="text-[#D6D0C5]">Maya Jones, maya@example.com</span>. Up to 50 at a time; emails go out a couple of seconds apart.</p>
      <textarea data-testid="bulk-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Maya Jones, maya@example.com\nSam Lee <sam@example.com>"} className={`${field} w-full font-mono`} />
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <label className="text-xs text-[#A7B0C0]">Months
          <select data-testid="bulk-months" value={months} onChange={(e) => setMonths(e.target.value)} className={`${field} block mt-1`}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#A7B0C0] flex-1 min-w-[200px]">Note for everyone (optional)
          <input data-testid="bulk-note" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} className={`${field} w-full mt-1`} />
        </label>
        <button data-testid="bulk-send" onClick={send} disabled={busy || !good.length || tooMany}
          className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium rounded-full px-5 py-2.5 transition-colors disabled:opacity-50">
          {busy && <Loader2 size={15} className="animate-spin" />} Send {good.length || ""} pass{good.length === 1 ? "" : "es"}
        </button>
      </div>
      {rows.length > 0 && (
        <div className="mt-3" data-testid="bulk-preview">
          <p className="text-xs text-[#A7B0C0] mb-1" data-testid="bulk-summary">{good.length} ready{rows.length - good.length ? ` · ${rows.length - good.length} will be skipped` : ""}{tooMany ? " · too many, max 50 per batch" : ""}</p>
          <ul className="max-h-48 overflow-y-auto text-xs space-y-0.5">
            {rows.map((r, i) => (
              <li key={i} data-testid={`bulk-row-${i}`} className={r.error ? "text-red-300" : "text-[#D6D0C5]"}>
                {r.error ? `✕ ${r.raw} (${r.error})` : `✓ ${r.name} · ${r.email}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
